import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DollarSign,
  Plus,
  Edit2,
  RefreshCw,
  Server,
  TrendingUp,
  AlertTriangle,
  Calculator,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';

interface ProviderCost {
  id: string;
  providerId: string;
  providerName: string;
  operationType: string;
  fixedCost: number;
  percentageCost: number;
  minimumCost: number;
  maximumCost?: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ProviderTemplate {
  id: string;
  name: string;
  type: string;
  category: string;
  version: string;
  status: string;
}

interface FormData {
  providerId: string;
  operationType: string;
  fixedCost: string;
  percentageCost: string;
  minimumCost: string;
  maximumCost: string;
  effectiveFrom: string;
  effectiveTo: string;
}

const OPERATION_TYPES = [
  { value: 'PIX_RECEIVE', label: 'Pix Recebido' },
  { value: 'PIX_SEND', label: 'Pix Enviado' },
  { value: 'TRANSFER', label: 'Transferência' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'CARD', label: 'Cartão' },
  { value: 'WITHDRAWAL', label: 'Saque' },
];

const OPERATION_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferência',
  BOLETO: 'Boleto',
  CARD: 'Cartão',
  WITHDRAWAL: 'Saque',
  MAINTENANCE: 'Manutenção',
};

const initialFormData: FormData = {
  providerId: '',
  operationType: 'PIX_RECEIVE',
  fixedCost: '0',
  percentageCost: '0',
  minimumCost: '0',
  maximumCost: '',
  effectiveFrom: new Date().toISOString().split('T')[0],
  effectiveTo: '',
};

export function ProviderCosts() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<ProviderCost | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [selectedProvider, setSelectedProvider] = useState<string>('');

  // Fetch provider templates for dropdown
  const { data: templates } = useQuery<ProviderTemplate[]>({
    queryKey: ['provider-templates'],
    queryFn: async () => {
      const res = await api.get('/provider-templates');
      // API returns { data: [], total: number }
      return res.data?.data || res.data || [];
    },
  });

  // Fetch provider costs - aggregate from all providers or filter by selected
  const { data: costs, isLoading, refetch } = useQuery<ProviderCost[]>({
    queryKey: ['provider-costs', selectedProvider, templates],
    queryFn: async () => {
      // If no templates loaded yet, return empty
      if (!templates || templates.length === 0) {
        return [];
      }
      
      // If a specific provider is selected, fetch only that one
      if (selectedProvider) {
        try {
          const res = await api.get(ENDPOINTS.costs.getProviderCosts(selectedProvider));
          const providerCosts = res.data || [];
          return providerCosts.map((cost: any) => ({
            ...cost,
            providerId: selectedProvider,
            providerName: templates.find(t => t.id === selectedProvider)?.name || selectedProvider,
          }));
        } catch {
          return [];
        }
      }
      
      // Otherwise, fetch costs for all templates
      const allCosts: ProviderCost[] = [];
      for (const template of templates) {
        try {
          const res = await api.get(ENDPOINTS.costs.getProviderCosts(template.id));
          const providerCosts = res.data || [];
          providerCosts.forEach((cost: any) => {
            allCosts.push({
              ...cost,
              providerId: template.id,
              providerName: template.name,
            });
          });
        } catch {
          // Provider may not have costs configured yet
        }
      }
      return allCosts;
    },
    enabled: !!templates && templates.length > 0,
  });

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const payload = {
        operationType: data.operationType,
        fixedCost: Math.round(parseFloat(data.fixedCost || '0') * 100),
        percentageCost: parseFloat(data.percentageCost || '0'),
        minimumCost: Math.round(parseFloat(data.minimumCost || '0') * 100),
        maximumCost: data.maximumCost ? Math.round(parseFloat(data.maximumCost) * 100) : undefined,
        effectiveFrom: data.effectiveFrom || undefined,
        effectiveTo: data.effectiveTo || undefined,
      };
      
      if (editingCost) {
        const res = await api.put(ENDPOINTS.costs.setProviderCost(editingCost.providerId), payload);
        return res.data;
      } else {
        const res = await api.post(ENDPOINTS.costs.setProviderCost(data.providerId), payload);
        return res.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-costs'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingCost(null);
  };

  const handleEdit = (cost: ProviderCost) => {
    setEditingCost(cost);
    setFormData({
      providerId: cost.providerId,
      operationType: cost.operationType,
      fixedCost: (cost.fixedCost / 100).toString(),
      percentageCost: cost.percentageCost.toString(),
      minimumCost: (cost.minimumCost / 100).toString(),
      maximumCost: cost.maximumCost ? (cost.maximumCost / 100).toString() : '',
      effectiveFrom: cost.effectiveFrom?.split('T')[0] || '',
      effectiveTo: cost.effectiveTo?.split('T')[0] || '',
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  // Group costs by provider
  const costsByProvider = costs?.reduce((acc, cost) => {
    if (!acc[cost.providerId]) {
      acc[cost.providerId] = [];
    }
    acc[cost.providerId].push(cost);
    return acc;
  }, {} as Record<string, ProviderCost[]>) || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Custos dos Providers</h1>
          <p className="text-surface-400 mt-1">
            Gerencie os custos cobrados pelos providers de pagamento
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedProvider}
            onChange={(e) => setSelectedProvider(e.target.value)}
            className="w-48"
          >
            <option value="">Todos Providers</option>
            {Array.isArray(templates) && templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.category})
              </option>
            ))}
          </Select>
          <Button
            variant="ghost"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Custo
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-orange-500/10 border-orange-500/30">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
          <div>
            <h3 className="font-medium text-orange-300">Impacto na Cascata de Taxas</h3>
            <p className="text-sm text-surface-400 mt-1">
              Alterações nos custos dos providers afetam toda a cadeia de taxas.
              As taxas da plataforma devem ser maiores que os custos do provider para garantir margem positiva.
            </p>
          </div>
        </div>
      </Card>

      {/* Provider Cards */}
      {Object.entries(costsByProvider).map(([providerId, providerCosts]) => (
        <Card key={providerId} className="overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-surface-700">
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Server className="w-5 h-5 text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                {(Array.isArray(templates) && templates.find((t) => t.id === providerId)?.name) || providerId}
              </h3>
              <p className="text-sm text-surface-400">
                {providerCosts.length} configurações de custo
              </p>
            </div>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 text-primary-500 animate-spin" />
            </div>
          ) : providerCosts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-surface-400">Nenhum custo configurado</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-surface-800">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Operação</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Custos</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Limites</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Status</th>
                  <th className="px-4 py-3 text-right text-sm font-medium text-surface-400">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-700">
                {providerCosts.map((cost) => (
                  <tr key={cost.id} className="hover:bg-surface-800/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calculator className="w-4 h-4 text-primary-400" />
                        <span className="font-medium text-white">
                          {OPERATION_LABELS[cost.operationType] || cost.operationType}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <DollarSign className="w-3 h-3 text-surface-400" />
                          <span className="text-surface-300">Fixo: {formatCurrency(cost.fixedCost)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <TrendingUp className="w-3 h-3 text-surface-400" />
                          <span className="text-surface-300">Percentual: {cost.percentageCost}%</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1 text-sm">
                        <div className="text-surface-300">Mín: {formatCurrency(cost.minimumCost)}</div>
                        {cost.maximumCost && (
                          <div className="text-surface-300">Máx: {formatCurrency(cost.maximumCost)}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={cost.isActive ? 'success' : 'warning'}>
                        {cost.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(cost)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ))}

      {/* Empty State */}
      {!isLoading && Object.keys(costsByProvider).length === 0 && (
        <Card className="text-center py-12">
          <Server className="w-16 h-16 text-surface-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Nenhum custo configurado
          </h3>
          <p className="text-surface-400 mb-4">
            Configure os custos dos providers para calcular as margens corretamente.
          </p>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar Custo
          </Button>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={editingCost ? 'Editar Custo' : 'Novo Custo do Provider'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingCost && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Provider
                </label>
                <Select
                  value={formData.providerId}
                  onChange={(e) => setFormData({ ...formData, providerId: e.target.value })}
                >
                  <option value="">Selecione um template...</option>
                  {Array.isArray(templates) && templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.category} - {t.type})
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-1">
                  Tipo de Operação
                </label>
                <Select
                  value={formData.operationType}
                  onChange={(e) => setFormData({ ...formData, operationType: e.target.value })}
                >
                  {OPERATION_TYPES.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Custo Fixo (R$)"
              type="number"
              step="0.01"
              min="0"
              value={formData.fixedCost}
              onChange={(e) => setFormData({ ...formData, fixedCost: e.target.value })}
              placeholder="0.00"
            />
            <Input
              label="Custo Percentual (%)"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={formData.percentageCost}
              onChange={(e) => setFormData({ ...formData, percentageCost: e.target.value })}
              placeholder="0.00"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Custo Mínimo (R$)"
              type="number"
              step="0.01"
              min="0"
              value={formData.minimumCost}
              onChange={(e) => setFormData({ ...formData, minimumCost: e.target.value })}
              placeholder="0.00"
            />
            <Input
              label="Custo Máximo (R$) - Opcional"
              type="number"
              step="0.01"
              min="0"
              value={formData.maximumCost}
              onChange={(e) => setFormData({ ...formData, maximumCost: e.target.value })}
              placeholder="Sem limite"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Vigência Início"
              type="date"
              value={formData.effectiveFrom}
              onChange={(e) => setFormData({ ...formData, effectiveFrom: e.target.value })}
            />
            <Input
              label="Vigência Fim (Opcional)"
              type="date"
              value={formData.effectiveTo}
              onChange={(e) => setFormData({ ...formData, effectiveTo: e.target.value })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {editingCost ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default ProviderCosts;
